import { useMemo } from 'react';
import { LICENSE_STATE_ACTIVE } from '@jira/platform__license-state/src/license-state.tsx';
import {
	SOFTWARE_PROJECT,
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
} from '@jira/platform__project-types/src/project-types.tsx';
import type { ChildIssueFieldKeys } from '@jira/issue__shared-types/src/TypesChildIssue.tsx';
import {
	useProjectKey as useProjectKeyNew,
	// eslint-disable-next-line jira/restricted/unsafe-global-state-stores
	useProjectType,
} from '@jira/issue__project-context-service/src/useProjectKey.tsx';
import {
	FULL_ISSUE,
	EMBED_ISSUE,
	WORK_ITEM_VIEW,
} from '@jira/platform__common-constants/src/analytics-sources.tsx';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { useLicenseStates } from '@atlassian/jira-tenant-context-controller/src/components/license-states/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useIssueContextStoreChildIssuesIssueLimitUrls,
	useIssueContextStoreChildIssuesLimit,
	useIssueContextStoreIsModal,
	useIssueContextStoreIssueKey,
	useIssueContextStoreIssueId,
	useIssueContextStoreAnalyticsSource,
	useIssueContextRapidViewMap,
	useIssueContextStoreMaxTokenLength,
	useIssueContextStoreIsLoadedWithPage,
	useIssueContextStoreEstimationStatistic,
	useIssueContextStoreIsLoading,
	useIssueContextStoreViewMode,
	useIssueContextStoreBackButtonUrl,
	useIssueContextStoreIssueNavigatorData,
	useIssueContextStoreBoardId,
	useIssueContextStoreBoardType,
	useIssueContextStoreIsSpaEnabled,
	useIssueContextStoreLoginRedirctUrl,
	useIssueContextStoreOnIssueKeyChange,
	useIssueContextStoreRapidViewId,
	useIssueContextStoreViewScreenId,
	useIssueContextStoreActions,
	useIssueContextStoreIsArchived,
	useIssueContextStoreIsClassifyEditEnabled,
	useIssueContextIsAiEnabledForIssue,
} from './useIssueTypeName.tsx';
import type {
	IssueContext,
	IssueNavigatorData,
	OnIssueKeyChangeFn,
} from './IssueContextServiceActions.types.tsx';

export const useChildIssuesLimit = (): number => {
	const [childIssuesLimit] = useIssueContextStoreChildIssuesLimit();
	return childIssuesLimit;
};

export const useChildIssuesIssueLimitUrls = ():
	| Record<ChildIssueFieldKeys, string | undefined>
	| undefined => {
	const [childIssuesIssueLimitUrls] = useIssueContextStoreChildIssuesIssueLimitUrls();
	return childIssuesIssueLimitUrls;
};

export const useChildIssuesIssueLimitUrl = (fieldId: ChildIssueFieldKeys): string | undefined => {
	const childIssuesIssueLimitUrls = useChildIssuesIssueLimitUrls();
	return childIssuesIssueLimitUrls?.[fieldId];
};

export const useIsModal = (): boolean => {
	const [isModal] = useIssueContextStoreIsModal();
	return isModal;
};

export const useIssueKey = (): string => {
	const [issueKey] = useIssueContextStoreIssueKey();
	return issueKey;
};
/**
 * There is a important pitfall to be validated when using this hook.
 * When transitioning to an issue which was already loaded previously, the issue-context-store takes time to sync the correct issueID from issue-store.
 * Until that happens any component that is using this will get incorrect issueId(either null or incorrect).
 * To ensure this does not lead to a bug, ensure that the issueId value being referenced is ALWAYS the latest and the correct reference is being used.
 */
export const useIssueId = (): string | undefined => {
	const [issueId] = useIssueContextStoreIssueId();
	return issueId;
};

export const useIsArchived = (): boolean | undefined => {
	const [isArchived] = useIssueContextStoreIsArchived();
	return isArchived;
};

export const useIsClassifyEditEnabled = (): boolean | undefined => {
	const [isClassifyEditEnabled] = useIssueContextStoreIsClassifyEditEnabled();
	return isClassifyEditEnabled;
};

/**
 * @deprecated Please use the useProjectKey hook from @jira/issue__project-context-service/src/main.tsx (BENTO-8752)
 * @returns {ProjectKey}
 */
export const useProjectKey = (): string => {
	const [issueKey] = useIssueContextStoreIssueKey();
	return issueKey.split('-')[0];
};

export const useAnalyticsSource = (): string | undefined => {
	const [analyticsSource] = useIssueContextStoreAnalyticsSource();
	return analyticsSource;
};

export const useIssueContextActions = (): readonly [
	undefined,
	{
		setIssueContext: (issueContext: IssueContext) => void;
		mergeIssueContext: (partialIssueContext: Partial<IssueContext>) => void;
		getChildIssuesLimit: () => number;
		getMaxTokenLength: () => number;
		getIsLoadedWithPage: () => boolean;
		getBackButtonUrl: () => string | undefined;
		getIsSpaEnabled: () => boolean;
		getIssueKey: () => string;
		setDetailViewAsModal: (willSave?: boolean | undefined) => void;
		setDetailViewAsSidebar: (willSave?: boolean | undefined) => void;
		resetDetailView: () => void;
	},
] => {
	const [
		,
		{
			setIssueContext,
			mergeIssueContext,
			getChildIssuesLimit,
			getMaxTokenLength,
			getIsLoadedWithPage,
			getBackButtonUrl,
			getIsSpaEnabled,
			getIssueKey,
			setDetailViewAsModal,
			setDetailViewAsSidebar,
			resetDetailView,
		},
	] = useIssueContextStoreActions() || [null, {}];

	const actions = useMemo(
		() => ({
			setIssueContext,
			mergeIssueContext,
			getChildIssuesLimit,
			getMaxTokenLength,
			getIsLoadedWithPage,
			getBackButtonUrl,
			getIsSpaEnabled,
			getIssueKey,
			setDetailViewAsModal,
			setDetailViewAsSidebar,
			resetDetailView,
		}),
		[
			setIssueContext,
			mergeIssueContext,
			getChildIssuesLimit,
			getMaxTokenLength,
			getIsLoadedWithPage,
			getBackButtonUrl,
			getIsSpaEnabled,
			getIssueKey,
			setDetailViewAsModal,
			setDetailViewAsSidebar,
			resetDetailView,
		],
	);
	return [undefined, actions] as const;
};

export const useRapidViewMap = (): string[][] | undefined => {
	const [rapidViewMap] = useIssueContextRapidViewMap();
	return rapidViewMap;
};

export const useMaxTokenLength = (): number => {
	const [maxTokenLength] = useIssueContextStoreMaxTokenLength();
	return maxTokenLength;
};

export const useIsLoadedWithPage = (): boolean => {
	const [isLoadedWithPage] = useIssueContextStoreIsLoadedWithPage();
	return isLoadedWithPage;
};

export const useEstimationStatistic = (): string | undefined => {
	const [estimationStatistic] = useIssueContextStoreEstimationStatistic();
	return estimationStatistic;
};

export const useIsLoading = (): boolean => {
	const [isLoading] = useIssueContextStoreIsLoading();
	return isLoading;
};

export const useViewMode = (): ('MODAL' | 'SIDEBAR' | 'DEFAULT') | undefined => {
	const [viewMode] = useIssueContextStoreViewMode();

	return viewMode;
};

export const useBackButtonUrl = (): string | undefined => {
	const [backButtonUrl] = useIssueContextStoreBackButtonUrl();
	return backButtonUrl;
};

export const useIssueNavigatorData = (): IssueNavigatorData | undefined => {
	const [issueNavigatorData] = useIssueContextStoreIssueNavigatorData();
	return issueNavigatorData;
};

export const useBoardId = (): number | undefined => {
	const [boardId] = useIssueContextStoreBoardId();
	return boardId;
};

export const useBoardType = (): string | undefined => {
	const [boardType] = useIssueContextStoreBoardType();
	return boardType;
};

export const useIsSpaEnabled = (): boolean => {
	const [isSpaEnabled] = useIssueContextStoreIsSpaEnabled();
	return isSpaEnabled;
};

export const useLoginRedirectUrl = (): string | undefined => {
	const [loginRedirectUrl] = useIssueContextStoreLoginRedirctUrl();
	return loginRedirectUrl;
};

export const useOnIssueKeyChange = (): OnIssueKeyChangeFn | undefined => {
	const [onIssueKeyChange] = useIssueContextStoreOnIssueKeyChange();
	return onIssueKeyChange;
};

export const useRapidViewId = (): number | undefined => {
	const [rapidViewId] = useIssueContextStoreRapidViewId();
	return rapidViewId;
};

export const useViewScreenId = (): number | undefined => {
	const [viewScreenId] = useIssueContextStoreViewScreenId();
	return viewScreenId;
};

export const useHasSoftwareAccess = (): boolean => {
	const licensedProducts = useLicensedProducts();
	const licenseStates = useLicenseStates();
	const isJiraSoftwareLicenseActive =
		licenseStates && licenseStates.software === LICENSE_STATE_ACTIVE;

	return licensedProducts[LICENSED_PRODUCTS.JIRA_SOFTWARE] && isJiraSoftwareLicenseActive;
};

export const useIsFullPageIssueView = (): boolean => {
	const analyticsSource = useAnalyticsSource();
	return analyticsSource === FULL_ISSUE;
};

export const useIsEmbedPageIssueView = (): boolean => {
	const analyticsSource = useAnalyticsSource();
	return analyticsSource === EMBED_ISSUE;
};

/**
 * @deprecated Use useIsWorkItemView from @jira/issue__util-hooks instead.
 */
export const useIsWorkItemView = (): boolean => {
	const analyticsSource = useAnalyticsSource();
	return analyticsSource === WORK_ITEM_VIEW;
};

/**
 * isAiEnabledForIssue is merged into the issue context store
 * at issue-fetch time, allowing faster retrieval.
 */
export const useIsAiEnabledForIssue = (): boolean => {
	const [isAiEnabledForIssue] = useIssueContextIsAiEnabledForIssue();
	return isAiEnabledForIssue;
};

export const useIsJiraIssue = (): boolean => {
	const issueKey = useIssueKey();
	const projectKey = useProjectKeyNew(issueKey);
	const projectType = useProjectType(projectKey);
	return [SOFTWARE_PROJECT, CORE_PROJECT].some((type) => type === projectType);
};

/**
 * @deprecated The hook is only being used for the migration from JSM to JSW/Core
 * and shouldn't be used otherwise as it will be removed once the migration is done
 */
export const useIsJSMIssue = (): boolean => {
	const issueKey = useIssueKey();
	const projectKey = useProjectKeyNew(issueKey);
	const projectType = useProjectType(projectKey);
	return projectType === SERVICE_DESK_PROJECT;
};

export const useShouldUseRelayIssueComments = (): boolean => {
	const isJiraIssue = useIsJiraIssue();
	const isJSMIssue = useIsJSMIssue();

	return isJiraIssue || (isJSMIssue && fg('migrate-to-graphql-from-rest-jsm-comments'));
};

export const useShouldUseRelayIssueAllActivity = (): boolean => {
	const isJiraIssue = useIsJiraIssue();

	return isJiraIssue || fg('migrate-all-activity-for-jsm');
};

export const useShouldUseRelayHistory = (): boolean => {
	const isJiraIssue = useIsJiraIssue();
	const isJSMIssue = useIsJSMIssue();

	return isJiraIssue || (isJSMIssue && fg('jsm-history-tab-relay-onboarding'));
};
